import React, { useState, useRef } from 'react';
import { Share } from 'lucide-react';
import { AstroViz, AstroResults, getElement, getPlanet, getZodiacSign, isRetrograde, getMoonPhase, getPersonalBurdenModifier } from './Astrology';
import { IdentityViz, identityOptions, getIdentityMessage, maxIdentityWeightsSum, renderIdentityViz, renderIdentityRock } from './Identity';
import { OptionChoice, Option, Factor, sumFactorWeights, weightToBucket } from "./choices";
import { behavioralOptions, renderBehaviorViz, BehaviorViz, getBehaviorMessage, maxBehavioralWeightsSum, renderBehaviorRock } from './Behavior';
import { Arrow, StickFigure } from './Stickfigure';
import { getRockName, RockContainer, RockElem, VizInner } from './Rocks';
import html2canvas from 'html2canvas';
import { isBrowser, isMobile } from 'react-device-detect';

const url = "sgi-w7m4.onrender.com"

const IdentityExperienceVisualizer = () => {
  const [identityFactors, setIdentityFactors] = useState<Factor[]>([]);
  const [behavioralFactors, setBehavioralFactors] = useState<Factor[]>([]);
  const [birthdate, setBirthdate] = useState('');
  const [astroResults, setAstroResults] = useState<AstroResults | null>(null);
  const [includeAstrology, setIncludeAstrology] = useState(true);
  const summaryRef = useRef<HTMLDivElement>(null);

  // Include CSS directly in the component to ensure it works when downloaded
  const css = `
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    
    .visualizer-container {
      max-width: 500px;
      margin: 0 auto;
      padding: 20px;
      background-color: #fff;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    
    .visualizer-header {
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    
    .visualizer-title {
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      color: #111827;
      margin-bottom: 8px;
    }
    
    .visualizer-subtitle {
      font-size: 16px;
      text-align: center;
      color: #6b7280;
      margin-bottom: 16px;
    }
    
    .visualizer-description {
      background-color: #f3f4f6;
      padding: 12px;
      border-radius: 8px;
      font-size: 14px;
      margin-bottom: 16px;
    }
    
    .visualizer-grid {
      display: flex;
      flex-direction: column;
      gap: 24px;
      margin-bottom: 24px;
    }
    
    .section-container {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 16px;
      background-color: #f9fafb;
    }
    
    .section-heading {
      font-size: 20px;
      font-weight: 600;
      text-align: center;
      margin-bottom: 16px;
      color: #111827;
    }
    
    .category-heading {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 8px;
      color: #374151;
    }
    
    .options-container {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 16px;
    }
    
    .option-button {
      padding: 4px 12px;
      font-size: 14px;
      border-radius: 9999px;
      border: none;
      cursor: pointer;
      background-color: #e5e7eb;
      color: #374151;
      transition: all 0.2s;
    }
    
    .option-button:hover {
      background-color: #d1d5db;
    }
    
    .option-button.selected-identity {
      background-color: #3b82f6;
      color: white;
    }
    
    .option-button.selected-behavior {
      background-color: #10b981;
      color: white;
    }
    
    .category-separator {
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 16px;
      margin-bottom: 16px;
    }
    
    .visualization-container {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
      background-color: #fff;
    }
    
    .visualization-inner {
      background-color: #f3f4f6;
      border-radius: 8px;
      padding: 24px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: auto;
      overflow: visible;
    }
    
    .message-box {
      text-align: center;
      max-width: 500px;
      padding: 12px 16px;
      background-color: white;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      margin-top: 16px;
    }
    
    .discussion-box {
      padding: 12px 16px;
      background-color: #fef3c7;
      border-radius: 8px;
      border: 1px solid #fde68a;
      font-size: 14px;
      margin-top: 16px;
    }
    
    .discussion-title {
      font-weight: 600;
      margin-bottom: 8px;
      font-size: 16px;
    }
    
    .discussion-list {
      padding-left: 20px;
    }
    
    .discussion-item {
      margin-bottom: 4px;
    }
    
    .astro-container {
      background: linear-gradient(to right, #1a1a2e, #16213e);
      color: #e6e6ff;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
    }
    
    .astro-heading {
      color: #e6e6ff;
      font-size: 20px;
      font-weight: 600;
      text-align: center;
      margin-bottom: 16px;
    }
    
    .astro-input-container {
      margin-bottom: 16px;
      text-align: center;
    }
    
    .astro-input-label {
      display: block;
      margin-bottom: 8px;
    }
    
    .astro-input {
      padding: 8px;
      border-radius: 4px;
      border: 1px solid #ccc;
      background: rgba(255, 255, 255, 0.9);
      color: #333;
      width: 200px;
    }
    
    .astro-content {
      padding: 16px;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.2);
      overflow: visible;
    }
    
    .astro-prompt {
      padding: 16px;
      text-align: center;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.2);
    }
    
    .astro-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 16px;
    }
    
    .astro-info {
      text-align: center;
    }
    
    .astro-sign {
      font-size: 20px;
      margin-bottom: 8px;
    }
    
    .astro-details {
      margin-bottom: 4px;
      text-align: left;
    }
    
    .astro-section {
      margin-bottom: 24px;
    }
    
    .astro-section-title {
      margin-bottom: 8px;
      font-size: 18px;
    }
    
    .astro-burden-display {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 8px;
      overflow: visible;
    }
    
    .astro-rock-label {
      margin-bottom: 4px;
      text-align: center;
    }
    
    .astro-message {
      font-size: 14px;
    }
    
    .rock-label {
      text-align: center;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 16px;
      z-index: 10;
      position: relative;
    }
    
    .rock-container {
      position: relative;
      overflow: visible;
      margin-bottom: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    
    .behavioral-viz {
      position: relative;
      width: 100%;
      height: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .impact-label {
      position: absolute;
      top: -10px;
      font-weight: bold;
      font-size: 16px;
      text-align: center;
      z-index: 10;
    }
    
    .astro-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
    }
    
    .toggle-switch {
      position: relative;
      display: inline-block;
      width: 50px;
      height: 24px;
      margin-left: 8px;
    }
    
    .toggle-switch input {
      opacity: 0;
      width: 0;
      height: 0;
    }
    
    .toggle-slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: #ccc;
      transition: .4s;
      border-radius: 24px;
    }
    
    .toggle-slider:before {
      position: absolute;
      content: "";
      height: 16px;
      width: 16px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
    
    input:checked + .toggle-slider {
      background-color: #2196F3;
    }
    
    input:checked + .toggle-slider:before {
      transform: translateX(26px);
    }
    
    .social-share-container {
      display: flex;
      gap: 10px;
      justify-content: center;
      margin-top: 16px;
    }
    
    .social-button {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px 12px;
      border-radius: 20px;
      border: none;
      color: white;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.2s;
    }
    
    .social-button.bluesky {
      background-color: #1d9bf0;
    }
    
    .social-button.twitter {
      background-color: #000000;
    }
    
    .social-button.instagram {
      background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
    }
    
    .social-button.tiktok {
      background-color: #000000;
    }
    
    .social-button:hover {
      opacity: 0.9;
      transform: translateY(-2px);
    }
    
    .footer {
      text-align: center;
      font-size: 14px;
      color: #6b7280;
      margin-top: 24px;
    }
    
    /* CSS for SVG elements to ensure they render correctly */
    svg {
      overflow: visible;
    }
    
    .rock-container {
      overflow: visible;
    }
    
    .stick-figure {
      overflow: visible;
    }

    .download-button {
      padding: 8px 16px;
      font-size: 14px;
      border-radius: 20px;
      border: none;
      cursor: pointer;
      background-color: #6b7280;
      color: white;
      font-weight: bold;
      transition: background-color 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .download-button:hover {
      background-color: #4b5563;
    }
  `;

  const handleSelectIdentityOption = (categoryId: string, option: OptionChoice) => {
    const filtered = identityFactors.filter(factor => factor.categoryId !== categoryId);

    setIdentityFactors([...filtered, {
      categoryId,
      option
    }]);
  };

  const handleSelectBehaviorOption = (categoryId: string, option: OptionChoice) => {
    const filtered = behavioralFactors.filter(factor => factor.categoryId !== categoryId);

    setBehavioralFactors([...filtered, {
      categoryId,
      option
    }]);
  };

  // Create a sharable summary of results
  const createSharableSummary = () => {
    // Get the highest weights for both categories
    let identityBurden = 0;
    let identityLabel = "None";
    if (identityFactors.length > 0) {
      const highestWeightFactor = [...identityFactors].sort((a, b) => b.option.weight - a.option.weight)[0];
      identityBurden = highestWeightFactor.option.weight;
      identityLabel = getRockName(identityBurden, "None");
    }

    let behaviorBurden = 0;
    let behaviorLabel = "None";
    if (behavioralFactors.length > 0) {
      const highestWeightFactor = [...behavioralFactors].sort((a, b) => b.option.weight - a.option.weight)[0];
      behaviorBurden = highestWeightFactor.option.weight;
      switch (weightToBucket(behaviorBurden, maxBehavioralWeightsSum)) {
        case 1: behaviorLabel = "Pebble"; break;
        case 2: behaviorLabel = "Stone"; break;
        case 3: behaviorLabel = "Boulder"; break;
        case 4: behaviorLabel = "Mountain"; break;
        default: behaviorLabel = "None"; break;
      }
    }

    // Get astrological data if available
    let astroBurden = 0;
    let astroBurdenOthers = 0;
    let zodiacSign = "";
    let astroLabel = "None";
    let astroOthersLabel = "None";

    if (astroResults && includeAstrology) {
      astroBurden = astroResults.personalBurden;
      astroBurdenOthers = astroResults.othersBurden;
      zodiacSign = astroResults.zodiacSign;

      switch (astroBurden) {
        case 1: astroLabel = "Pebble"; break;
        case 2: astroLabel = "Stone"; break;
        case 3: astroLabel = "Boulder"; break;
        case 4: astroLabel = "Mountain"; break;
        default: astroLabel = "None"; break;
      }

      switch (astroBurdenOthers) {
        case 1: astroOthersLabel = "Pebble"; break;
        case 2: astroOthersLabel = "Stone"; break;
        case 3: astroOthersLabel = "Boulder"; break;
        case 4: astroOthersLabel = "Mountain"; break;
        default: astroOthersLabel = "None"; break;
      }
    }

    // Maximum rock size to prevent overflow
    const MAX_ROCK_SIZE = 60; // Reduced from 120

    // Function to draw rocks
    const renderRock = (size: number, color: string, label: string) => {
      const rockShape = "M10,2 Q20,-2 30,3 Q42,5 46,15 Q50,25 45,35 Q40,45 28,48 Q18,50 10,45 Q0,38 2,25 Q0,15 10,2";

      // Scale the rock appropriately to prevent overflow
      let rockSize = 0;
      if (size > 0) {
        const baseSize = 30;
        const scale = Math.min(1.6, 0.8 + (size * 0.2)); // Reduced scaling factor
        rockSize = Math.min(MAX_ROCK_SIZE, baseSize * scale);
      }

      return (
        <div style={{ margin: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'auto', flex: '0 0 auto', height: 'auto' }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
            {label}
          </div>
          {size > 0 && (
            <div style={{ overflow: 'visible' }}>
              <svg width={rockSize} height={rockSize} viewBox="0 0 50 50">
                <path
                  d={rockShape}
                  fill={color === "earth" ? "#a98274" : color === "impact" ? "#e05d44" : "#9370db"}
                  stroke="#333"
                  strokeWidth="0.5"
                />
              </svg>
            </div>
          )}
        </div>
      );
    };

    // Return the sharable summary component - wrapped in a div for ref attachment later
    return (
      <div className="visualization-container" style={{ background: '#f8f9fa', padding: '20px', paddingTop: '5px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        <h5 style={{ width: "100%", fontSize: '12px', color: '#bbb', paddingBottom: '10px' }}>
          <span style={{ display: "inline-block" }}>Social Footprint Index (SFI)</span>
          <span style={{ float: "right" }}>{url}</span>
        </h5>
        <h2 style={{ textAlign: 'center', fontSize: '24px', marginBottom: '10px', color: '#333' }}>
          🪨 PRIVILEGE EXPOSED 🪨
        </h2>

        <p style={{ textAlign: 'center', fontSize: '16px', marginBottom: '10px', fontStyle: 'italic', color: '#555' }}>
          What I carry, what I cause. The raw truth of my social footprint.
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', marginBottom: '0px' }}>
          <div style={{ textAlign: 'center', flex: '1', minWidth: '150px', maxWidth: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: '#444', paddingBottom: '5px' }}>My Identity Burden</h3>
            {renderIdentityRock({ factors: identityFactors })}
          </div>

          <div style={{ textAlign: 'center', flex: '1', minWidth: '150px', maxWidth: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: '#444', paddingBottom: '5px' }}>My Impact on Others</h3>
            {renderBehaviorRock({ factors: behavioralFactors })}
          </div>
        </div>

        {astroResults && includeAstrology && (
          <div style={{ marginTop: '00px', paddingTop: '10px', background: 'rgba(0, 0, 0, 0.05)', borderRadius: '8px', overflow: 'hidden' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '10px', color: '#444' }}>
              {zodiacSign} Cosmic Influences
            </h3>

            <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center', flex: '1', minWidth: '150px', maxWidth: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'visible' }}>
                <h4 style={{ color: '#555', fontSize: '14px' }}>My Cosmic Burden</h4>
                {renderRock(astroBurden, "astro", astroLabel)}
              </div>

              <div style={{ textAlign: 'center', flex: '1', minWidth: '150px', maxWidth: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'visible' }}>
                <h4 style={{ color: '#555', fontSize: '14px' }}>My Cosmic Impact</h4>
                {renderRock(astroBurdenOthers, "astro", astroOthersLabel)}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: '10px', textAlign: 'center', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
          <p style={{ fontSize: '12px', color: '#777', marginBottom: '10px' }}>Generated with Social Footprint Index (SFI)</p>
          <p style={{ fontSize: '10px', color: '#888' }}>
            #PrivilegeExposed #SocialFootprintIndex #SFI #WakeUpAndSeeTheStones
          </p>
        </div>
      </div>
    );
  };

  // Generate shareable URL
  const shareToSocialMedia = (platform: string) => {
    // Use createSharableSummary to get current data for the text
    let identityBurden = 0;
    let identityLabel = "None";
    if (identityFactors.length > 0) {
      const highestWeightFactor = [...identityFactors].sort((a, b) => b.option.weight - a.option.weight)[0];
      identityBurden = highestWeightFactor.option.weight;
      identityLabel = getRockName(identityBurden, "None");
    }

    let behaviorBurden = 0;
    let behaviorLabel = "None";
    if (behavioralFactors.length > 0) {
      const highestWeightFactor = [...behavioralFactors].sort((a, b) => b.option.weight - a.option.weight)[0];
      behaviorBurden = highestWeightFactor.option.weight;
      behaviorLabel = getRockName(behaviorBurden, "None");
    }
    // Note: Astro data isn't included in the default share text, so no need to recalculate here unless desired.

    let shareText = `I'm a ${identityLabel} with a ${behaviorLabel} impact. Get your Social Footprint Index (SFI)!\n(Please download results image and attach!)`;
    let shareTextWithUrl = `I'm a ${identityLabel} with a ${behaviorLabel} impact. Get your Social Footprint Index (SFI) at https://${url}!\n(Please downlaod results image and attach!)`;
    let shareUrl = "";

    switch (platform) {
      case 'twitter':
        var baseUrl = isMobile ? "twitter://" : "https://x.com/";
        shareUrl = `${baseUrl}intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent("https://" + url)}`;
        break;
      case 'bluesky':
        var baseUrl = isMobile ? "bluesky://" : "https://bsky.app/";
        shareUrl = `${baseUrl}intent/compose?text=${encodeURIComponent(shareTextWithUrl)}`;
        break;
      case 'instagram':
        alert("To share to Instagram Stories, please download/screenshot and post!");
        return;
      case 'tiktok':
        alert("To share to TikTok, please download/screenshot and post!");
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank');
  };

  // Calculate astrological burden based on birthdate
  const getAstrologyBurden = (date: string) => {
    // Parse the date
    const birthDate = new Date(date);
    const birthMonth = birthDate.getMonth() + 1; // JS months are 0-based
    const birthDay = birthDate.getDate();

    // Get current date factors
    const today = new Date();
    const currentDay = today.getDay();
    const currentDate = today.getDate();
    const currentHour = today.getHours();

    const zodiacSign = getZodiacSign(birthMonth, birthDay);
    let element = getElement(zodiacSign);
    const rulingPlanet = getPlanet(zodiacSign);

    // For more varied results, use birth date to determine a base influence
    // This ensures different birth dates get different results
    // Using the sum of month and day digits for more variation
    const birthDateSum = birthMonth + birthDay;
    const baseBurden = (birthDateSum % 4) + 1;

    const rulingPlanetRetrograde = isRetrograde(rulingPlanet, today);
    const [moonPhaseIndex, moonPhaseName] = getMoonPhase(currentDate);
    const mercuryRetrograde = isRetrograde("Mercury", today);

    const personalBurdenModifier = getPersonalBurdenModifier(element, moonPhaseIndex, mercuryRetrograde, rulingPlanetRetrograde, currentHour);
    const personalBurden = Math.min(4, Math.max(1, baseBurden + personalBurdenModifier));

    // For others burden, use a different formula to ensure variety
    // Use birth month and day to create more variety
    const othersBurdenBase = ((birthMonth * birthDay) % 4) + 1;

    // Apply modifiers to others burden
    let othersBurdenModifier = 0;

    // Current day of week affects others burden
    const dayModifier = (currentDay % 4) - 2; // Range from -2 to +2
    othersBurdenModifier += dayModifier;

    // Element affects outward burden
    if (element === "Fire" || element === "Air") othersBurdenModifier += 1;
    else if (element === "Water") othersBurdenModifier -= 1;

    // Moon phase affects others burden differently
    if (moonPhaseIndex === 4) othersBurdenModifier += 1; // Full moon increases outward impact
    else if (moonPhaseIndex === 0) othersBurdenModifier -= 1; // New moon decreases outward impact

    // Calculate final others burden, ensuring it's between 1-4
    const othersBurden = Math.min(4, Math.max(1, othersBurdenBase + othersBurdenModifier));

    // Return comprehensive astrological data
    return {
      element,
      mercuryRetrograde,
      moonPhaseName,
      moonPhaseIndex,
      personalBurden,
      othersBurden,
      rulingPlanet,
      rulingPlanetRetrograde,
      zodiacSign,
    };
  };

  const handleBirthdateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBirthdate(e.target.value);

    // Only calculate if we have a valid date
    if (e.target.value) {
      try {
        const results = getAstrologyBurden(e.target.value);
        setAstroResults(results);
      } catch (err) {
        console.error("Invalid date format", err);
      }
    }
  };

  // Check if all identity and behavioral questions have been answered
  const isIdentityComplete = identityFactors.length === identityOptions.length;
  const isBehaviorComplete = behavioralFactors.length === behavioralOptions.length;
  const isFormComplete = isIdentityComplete && isBehaviorComplete;

  // Function to handle downloading the summary as an image
  const handleDownloadImage = async () => {
    if (!summaryRef.current) {
      console.error("Summary element not found");
      return;
    }

    try {
      // Temporarily ensure visibility for accurate capture if needed
      // summaryRef.current.style.display = 'block';

      const canvas = await html2canvas(summaryRef.current, {
        backgroundColor: '#f8f9fa', // Match background
        useCORS: true, // If there are external images/fonts
        logging: false, // Disable logging to console
        scale: 2, // Increase resolution
        onclone: (document) => {
          // Optional: Apply specific styles only during capture if needed
          // e.g., document.querySelector('.some-element').style.visibility = 'hidden';
        }
      });

      // Restore original display if changed
      // summaryRef.current.style.display = ''; // Or original display value

      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = 'sfi-results.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating image:', error);
      alert('Sorry, there was an error creating the image for download.');
    }
  };

  return (
    <>
      {/* Include the CSS */}
      <style>{css}</style>

      <div className="visualizer-container">
        <div className="visualizer-header">
          <h1 className="visualizer-title">
            Social Footprint Index (SFI)
          </h1>
          <h2 className="visualizer-subtitle">
            Understanding privilege, oppression, and impact through natural metaphors
          </h2>

          {/* Always visible description */}
          <p className="visualizer-description" style={{ textAlign: 'center' }}>
            This tool uses rocks instead of numbers to visualize social burdens, rejecting neo-colonialist ontologies of quantification.
            <br />
            <br />
            Rocks symbolize the weight of oppression in a more natural, tangible way that connects to indigenous wisdom and earth-centered perspectives.
            <br />
            <br />
            Some of us carry pebbles, little or no burden that doesn't affect our life.
            Some of us carry boulders, crushing burdens that lead to adverse lived experiences.
          </p>
        </div>

        {/* Main content */}
        <div className="visualizer-grid">
          {/* Identity Factors Section */}
          <div className="section-container">
            <h2 className="section-heading">Identity Factors: How Society Affects You</h2>

            <div>
              {identityOptions.map((category, index) => (
                <div key={category.id} className={index < identityOptions.length - 1 ? 'category-separator' : ''}>
                  <h3 className="category-heading">{category.label}</h3>
                  <div className="options-container">
                    {category.options.map(option => {
                      const isSelected = identityFactors.some(f =>
                        f.categoryId === category.id && f.option.value === option.value
                      );

                      return (
                        <button key={option.value} onClick={() => handleSelectIdentityOption(category.id, option)}
                          className={`option-button ${isSelected ? 'selected-identity' : ''}`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Identity Burden Visualization */}
          <div className="visualization-container">
            <h2 className="section-heading">Your Identity Burden</h2>
            <div style={{ display: 'flex' }}>
              {renderIdentityViz(identityFactors)}
            </div>
          </div>

          {/* Behavioral Factors Section */}
          <div className="section-container">
            <h2 className="section-heading">Behavioral Choices: How You Affect Others</h2>

            <div>
              {behavioralOptions.map((category, index) => (
                <div key={category.id} className={index < behavioralOptions.length - 1 ? 'category-separator' : ''}>
                  <h3 className="category-heading">{category.label}</h3>
                  <div className="options-container">
                    {category.options.map(option => {
                      const isSelected = behavioralFactors.some(f =>
                        f.categoryId === category.id && f.option.value === option.value
                      );

                      return (
                        <button key={option.value} onClick={() => handleSelectBehaviorOption(category.id, option)}
                          className={`option-button ${isSelected ? 'selected-behavior' : ''}`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Behavioral Impact Visualization */}
          <div className="visualization-container" style={{ marginBottom: '0' }}>
            <h2 className="section-heading">Your Impact on Others</h2>
            <div style={{ overflow: 'visible' }}>
              {renderBehaviorViz({ factors: behavioralFactors })}
            </div>
          </div>

          {/* Astrological Burden Section */}
          <div className="astro-container">
            <h2 className="astro-heading">Astrological Burden</h2>

            {/* Birthdate input */}
            <div className="astro-input-container">
              <label className="astro-input-label">
                Enter your birthdate:
              </label>
              <input type="date" value={birthdate} onChange={handleBirthdateChange} className="astro-input" />
            </div>

            {!astroResults ? (
              <div className="astro-prompt">
                Enter your birthdate to see your astrological burden
              </div>
            ) : (<AstroViz astroResults={astroResults} />)
            }
          </div>

          {/* Sharable Summary Section */}
          <div className="section-container">
            <h2 className="section-heading">Share Your Results</h2>

            {/* Conditionally render summary or prompt */}
            {!isFormComplete ? (
              <p style={{ textAlign: 'center', marginBottom: '20px', color: 'red', fontWeight: 'bold' }}>
                Please answer all Identity and Behavioral questions to see your results summary.
              </p>
            ) : (
              <>
                {astroResults && (
                  <div className="astro-toggle" style={{ margin: '0 0 16px 0' }}>
                    <span>Include Astrological Data:</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeAstrology}
                        onChange={() => setIncludeAstrology(!includeAstrology)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                )}

                <p style={{ textAlign: 'center', marginBottom: '20px' }}>
                  Take a screenshot or download the image below to share!
                </p>

                {/* Attach ref to a div wrapping the summary component */}
                <div ref={summaryRef} style={{ marginBottom: '16px' }}>
                  {createSharableSummary()}
                </div>

                {/* Download Button */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                   <button onClick={handleDownloadImage} className="download-button">
                     <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                       <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                       <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                     </svg>
                     Download Results Image
                   </button>
                </div>

                {/* Social Share Buttons - Moved here */}
                <div className="social-share-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <h1 style={{ display: 'flex', flex: 1 }}>Share to Socials</h1>
                  <div style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'center', gap: '10px' }}>
                    <button className="social-button bluesky" onClick={() => shareToSocialMedia('bluesky')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '5px' }}>
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="white" />
                        <path d="M9 16L15 8M9 8H15V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Share to Bluesky
                    </button>
                    <button className="social-button tiktok" onClick={() => shareToSocialMedia('tiktok')}>
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '5px' }}>
                         <path d="M21 8V16C21 18.7614 18.7614 21 16 21H8C5.23858 21 3 18.7614 3 16V8C3 5.23858 5.23858 3 8 3H16C18.7614 3 21 5.23858 21 8Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                         <path d="M10 12C8.34315 12 7 13.3431 7 15C7 16.6569 8.34315 18 10 18C11.6569 18 13 16.6569 13 15V6C13.3333 7 14.6 9 17 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                       </svg>
                      TikTok
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'center', gap: '10px' }}>
                    <button className="social-button twitter" onClick={() => shareToSocialMedia('twitter')}>
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '5px' }}>
                         <path d="M22 4.01C21 4.5 20.02 4.69 19 5C17.879 3.735 16 3.665 14.14 4.382C12.28 5.1 11.029 6.811 11 8.76V9.76C7.75 9.83 5.02 8.43 3 6.01C3 6.01 -1 14.01 8 18.01C6 19.38 4 20.01 2 20.01C5 22.01 8 23.01 11 23.01C15.226 23.01 19.2335 21.107 21.2905 17.3615C23.1205 14.012 23.1905 10.0125 22.25 7.01C21.58 6.01 21.75 5.01 22 4.01Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                       </svg>
                      Share to X
                    </button>
                    <button className="social-button instagram" onClick={() => shareToSocialMedia('instagram')}>
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '5px' }}>
                         <rect x="2" y="2" width="20" height="20" rx="5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                         <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                         <path d="M18 6L18 6.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                       </svg>
                      IG Stories
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Discussion Points */}
        <div className="discussion-box" style={{ margin: '0 0 24px 0' }}>
          <h3 className="discussion-title">Discussion Points:</h3>
          <ul className="discussion-list">
            <li className="discussion-item">How might identity factors interact with each other in real life?</li>
            <li className="discussion-item">How do our everyday choices affect communities we may never see?</li>
            <li className="discussion-item">What barriers prevent people from making more ethical choices?</li>
            <li className="discussion-item">How are privilege and consumption patterns connected?</li>
            <li className="discussion-item">How might astrological beliefs influence people's social impact?</li>
            <li className="discussion-item">How can we continue moving past western paradigms of thought?</li>
          </ul>
        </div>

        <div className="footer">
          <p>This tool is for educational purposes only and is meant to facilitate discussion about diversity, equity, inclusion, and social responsibility.</p>
          <p>The visualization is a simplified representation and cannot capture the full complexity of human experience.</p>
        </div>
      </div>
    </>
  );
};

export default IdentityExperienceVisualizer;
